import axios from '../config/axios'
import { EMAIL_CONFIG, isEmailNotificationEnabled } from '../config/emailConfig'

export interface EmailNotificationPayload {
  to: string
  subject: string
  eventTitle: string
  eventDetails: string
  eventDate: string
  eventVenue: string
  creatorName?: string
  recipientName?: string
  eventImageUrl?: string
  eventId?: number
}

export interface TaskEmailNotificationPayload {
  to: string
  subject: string
  template?: string
  taskTitle: string
  taskDetails: string
  taskDueDate: string
  taskDueCountdown?: string
  taskImportance: string
  taskLink?: string
  taskAppUrl?: string
  creatorName?: string
  recipientName?: string
  taskId?: number
  devNote?: string
}

export interface EmailFailureCallback {
  (message: string): void
}

export const sendEventCreationNotification = async (
  payload: EmailNotificationPayload,
  onEmailFailure?: EmailFailureCallback
): Promise<void> => {
  // Check if email notifications are enabled
  if (!isEmailNotificationEnabled()) {
    console.log('Email notifications are disabled')
    return
  }

  try {
    const emailData = {
      to: payload.to,
      subject: payload.subject,
      template: EMAIL_CONFIG.TEMPLATES.EVENT_CREATION,
      data: {
        eventTitle: payload.eventTitle,
        eventDetails: payload.eventDetails,
        eventDate: payload.eventDate,
        eventVenue: payload.eventVenue,
        creatorName: payload.creatorName || 'Event Creator',
        recipientName: payload.recipientName || '',
        eventImageUrl: payload.eventImageUrl,
        fromName: EMAIL_CONFIG.DEFAULTS.FROM_NAME,
        replyTo: EMAIL_CONFIG.DEFAULTS.REPLY_TO
      },
      metadata: {
        eventId: payload.eventId
      }
    }

    console.log('📧 Sending email with data:', JSON.stringify(emailData, null, 2))
    console.log('🖼️ Image URL being sent to backend:', payload.eventImageUrl)

    const response = await axios.post('/notifications/send-email', emailData)
    console.log('Event creation notification sent successfully')
    console.log('📧 Backend response:', response.data)
  } catch (error: any) {
    console.error('Failed to send event creation notification:', error)

    // Always show failure message when email fails
    if (onEmailFailure) {
      onEmailFailure('Email not sent')
    }

    console.log('📧 Email notification failed')
    console.log('📧 Email would have been sent to:', payload.to)
    console.log('📧 Email subject:', payload.subject)
    console.log('📧 Event details:', {
      title: payload.eventTitle,
      date: payload.eventDate,
      venue: payload.eventVenue,
      imageUrl: payload.eventImageUrl,
      eventId: payload.eventId
    })
    // Don't throw error to prevent breaking the event creation flow
  }
}

export const sendTaskCreationNotification = async (
  payload: TaskEmailNotificationPayload,
  onEmailFailure?: EmailFailureCallback
): Promise<void> => {
  // Check if email notifications are enabled
  if (!isEmailNotificationEnabled()) {
    console.log('Email notifications are disabled')
    return
  }

  try {
    const emailData = {
      to: payload.to,
      subject: payload.subject,
      template: payload.template || EMAIL_CONFIG.TEMPLATES.TASK_CREATION,
      data: {
        taskTitle: payload.taskTitle,
        taskDetails: payload.taskDetails,
        taskDueDate: payload.taskDueDate,
        taskDueCountdown: payload.taskDueCountdown || '',
        taskImportance: payload.taskImportance,
        taskLink: payload.taskLink || '',
        taskAppUrl: payload.taskAppUrl || '',
        creatorName: payload.creatorName || 'Task Creator',
        recipientName: payload.recipientName || '',
        fromName: EMAIL_CONFIG.DEFAULTS.FROM_NAME,
        replyTo: EMAIL_CONFIG.DEFAULTS.REPLY_TO,
        devNote: payload.devNote || ''
      },
      metadata: {
        taskId: payload.taskId
      }
    }

    console.log('📧 Sending task creation email with data:', JSON.stringify(emailData, null, 2))

    const response = await axios.post('/notifications/send-email', emailData)
    console.log('Task creation notification sent successfully')
    console.log('📧 Backend response:', response.data)
  } catch (error: any) {
    console.error('Failed to send task creation notification:', error)

    if (onEmailFailure) {
      onEmailFailure('Email not sent')
    }

    console.log('📧 Task email notification failed')
    console.log('📧 Email would have been sent to:', payload.to)
    console.log('📧 Email subject:', payload.subject)
    console.log('📧 Task details:', {
      title: payload.taskTitle,
      dueDate: payload.taskDueDate,
      importance: payload.taskImportance,
      taskId: payload.taskId
    })
    // Don't throw error to prevent breaking the task creation flow
  }
}

/**
 * Returns a human-readable countdown string until the due date.
 * - If more than or equal to 24 hours away → "X days" (rounded down)
 * - If less than 24 hours away → "X hours" (rounded down)
 * - If already past → "overdue"
 */
export const formatTaskDueCountdown = (dueDate: Date | string | null): string => {
  if (!dueDate) return ''

  const now = new Date()
  const due = new Date(dueDate)
  const diffMs = due.getTime() - now.getTime()

  if (diffMs <= 0) return 'overdue'

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'}`
  }

  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  return `${diffDays} day${diffDays === 1 ? '' : 's'}`
}

export const formatTaskDueDateForEmail = (dueDate: Date | string | null): string => {
  if (!dueDate) return 'No due date set'

  const date = new Date(dueDate)
  const formatOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  }
  return date.toLocaleString('en-US', formatOptions)
}

export const formatEventDateForEmail = (startDate: Date | string, endDate: Date | string): string => {
  const start = new Date(startDate)
  const end = new Date(endDate)

  const formatOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  }

  const startFormatted = start.toLocaleDateString('en-US', formatOptions)
  const endFormatted = end.toLocaleDateString('en-US', formatOptions)

  // If same day, show time range
  if (start.toDateString() === end.toDateString()) {
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    }
    const endTime = end.toLocaleTimeString('en-US', timeOptions)
    return `${startFormatted} - ${endTime}`
  }

  return `${startFormatted} to ${endFormatted}`
}
